/**
 * Logique partagée : repérage des noms (identification patient) et anonymisation.
 * Utilisée par le générateur de bilan et la page Patients.
 * Modifier ce fichier pour changer le comportement sur tout le site.
 */

import type { ExtractionResult } from "@/lib/text-extractor";
import { extractTextFromFile } from "@/lib/text-extractor";
import { anonymizeText, type AnonymizationResult } from "@/lib/anonymizer";
import { getNonPatientNamesForAnonymization } from "@/lib/patient-declensions";

/** Nombre de caractères du document envoyés comme contexte à l'API identify-patient (améliore l'identification). */
export const DOCUMENT_CONTEXT_MAX_LENGTH = 2000;

export interface PatientResult {
  format: string;
  value: string;
  anonymousId?: string;
}

export interface IdentifyAndAnonymizeOptions {
  /** Fichier à traiter */
  file: File;
  /** Noms à exclure (orthoptistes / rédacteurs) — un par élément */
  practitioners: string[];
  practiceAddress?: string;
  /** Nombre de caractères du document envoyés comme contexte à l'API (défaut 2000). Améliore l'identification patient. */
  documentContextMaxLength?: number;
  /** Déclinaisons supplémentaires du patient (ex. depuis la DB) à inclure dans la correspondance d'anonymisation */
  extraDeclensions?: string[] | null;
  /** Ou : callback pour récupérer les déclinaisons à partir du nom du patient (ex. fetch API patients) */
  getDeclensionsForName?: (patientName: string) => Promise<string[] | null>;
  /** Si true (défaut), les noms détectés qui ne sont pas le patient sont aussi anonymisés comme praticiens */
  includeOtherNamesInAnonymization?: boolean;
}

export interface IdentifyAndAnonymizeResult {
  extraction: ExtractionResult;
  identifiedResults: PatientResult[];
  anonymized: AnonymizationResult;
  patientName: string;
  error?: undefined;
}

export interface IdentifyAndAnonymizeError {
  error: string;
  extraction?: ExtractionResult;
}

function generateAnonymousId(length = 8): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++)
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  return result;
}

/**
 * Génère une table de correspondance (patient → code anonyme) à partir des résultats de l'API identify-patient.
 */
export function generateCorrespondenceTable(
  results: { format: string; value: string }[]
): PatientResult[] {
  const patientPrefix = generateAnonymousId(4);
  return results.map((item, index) => ({
    ...item,
    anonymousId: `${patientPrefix}-${String(index + 1).padStart(2, "0")}`,
  }));
}

/**
 * Pipeline complet : extraction → identification patient (avec contexte document) → anonymisation.
 * Même logique que dans le générateur de bilan (documentContext améliore l'identification).
 */
export async function runIdentifyAndAnonymize(
  options: IdentifyAndAnonymizeOptions
): Promise<IdentifyAndAnonymizeResult | IdentifyAndAnonymizeError> {
  const {
    file,
    practitioners,
    practiceAddress = "",
    documentContextMaxLength = DOCUMENT_CONTEXT_MAX_LENGTH,
    extraDeclensions: extraDeclensionsOption = null,
    getDeclensionsForName,
    includeOtherNamesInAnonymization = true,
  } = options;

  const extraction = await extractTextFromFile(file);

  if (extraction.names.length === 0) {
    return { error: "Aucun nom détecté dans le document.", extraction };
  }

  const documentContext = extraction.text.substring(0, documentContextMaxLength);

  const identifyRes = await fetch("/api/identify-patient", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      detectedNames: extraction.names,
      practitioners,
      practiceAddress,
      documentContext,
    }),
  });

  const identifyData = await identifyRes.json();

  if (!identifyRes.ok || !identifyData.results) {
    return {
      error: identifyData.error || "Erreur identification patient",
      extraction,
    };
  }

  const resultsWithIds = generateCorrespondenceTable(identifyData.results);
  const patientValues = resultsWithIds.map((r) => r.value).filter(Boolean);
  const patientNameForDecl = patientValues.slice(0, 2).join(" ");

  let extraDeclensions = extraDeclensionsOption ?? null;
  if (getDeclensionsForName && patientNameForDecl) {
    try {
      const decl = await getDeclensionsForName(patientNameForDecl);
      if (decl?.length) extraDeclensions = decl;
    } catch {
      // ignore
    }
  }

  const baseCorrespondence = resultsWithIds
    .filter((r) => r.value && r.anonymousId)
    .map((r) => ({ original: r.value, anonymousId: r.anonymousId! }));

  const existing = new Set(baseCorrespondence.map((c) => c.original.toLowerCase()));
  const firstCode = baseCorrespondence[0]?.anonymousId;
  const declEntries =
    firstCode && extraDeclensions?.length
      ? extraDeclensions
          .map((d) => (typeof d === "string" ? d.trim() : ""))
          .filter((d) => d && !existing.has(d.toLowerCase()))
          .map((original) => ({ original, anonymousId: firstCode }))
      : [];

  const correspondence = [...baseCorrespondence, ...declEntries];

  const practitionersForAnonymize =
    includeOtherNamesInAnonymization
      ? [...practitioners, ...getNonPatientNamesForAnonymization(extraction.names, patientValues)]
      : practitioners;

  const anonymized = anonymizeText(extraction.text, correspondence, practitionersForAnonymize);
  const patientName = patientNameForDecl || "";

  return {
    extraction,
    identifiedResults: resultsWithIds,
    anonymized,
    patientName,
  };
}
